<template>
  <div class="container my-4 practice-records">
    <h1 class="page-title mb-4">Practice Records</h1>

    <LoadingSpinner v-if="loading" message="Loading your practice records..." />

    <ErrorMessage
      v-else-if="errorMessage"
      title="Could not load your records"
      :message="errorMessage"
      @retry="load"
    />

    <EmptyState
      v-else-if="!isAuthenticated"
      icon="bi-person-lock"
      title="Sign in to view your records"
      message="Your practice history, scores and statistics appear here once you are signed in."
    />

    <template v-else>
      <!-- Statistics -->
      <div class="row g-3 mb-4">
        <div class="col-6 col-md-3">
          <div class="stat-card">
            <div class="stat-value">{{ stats.total_sessions }}</div>
            <div class="stat-label">Total attempts</div>
          </div>
        </div>
        <div class="col-6 col-md-3">
          <div class="stat-card">
            <div class="stat-value">{{ stats.graded_sessions }}</div>
            <div class="stat-label">Graded</div>
          </div>
        </div>
        <div class="col-6 col-md-3">
          <div class="stat-card">
            <div class="stat-value">{{ stats.average_band ?? "—" }}</div>
            <div class="stat-label">Average band</div>
          </div>
        </div>
        <div class="col-6 col-md-3">
          <div class="stat-card">
            <div class="stat-value">{{ skillCount }}</div>
            <div class="stat-label">Skills practised</div>
          </div>
        </div>
      </div>

      <!-- History -->
      <EmptyState
        v-if="!history.length"
        icon="bi-clipboard-x"
        title="No attempts yet"
        message="Start a mock exam to begin building your practice history."
      />

      <div v-else class="table-responsive">
        <table class="table align-middle records-table">
          <thead>
            <tr>
              <th>Skill</th>
              <th>Status</th>
              <th>Band</th>
              <th>Started</th>
              <th>Submitted</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in history" :key="row.id">
              <td class="text-capitalize">{{ row.skill }}</td>
              <td>
                <span class="badge" :class="statusClass(row.status)">
                  {{ formatStatus(row.status) }}
                </span>
              </td>
              <td>{{ row.score ? row.score.band_score : "—" }}</td>
              <td>{{ formatDate(row.started_at) }}</td>
              <td>{{ formatDate(row.submitted_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { getHistory, getStatistics } from "@/api/users";
import { useAuth } from "@/composables/useAuth";
import { handleError } from "@/services/errorHandler";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import ErrorMessage from "@/components/common/ErrorMessage.vue";
import EmptyState from "@/components/common/EmptyState.vue";

const { isAuthenticated, init } = useAuth();

const loading = ref(false);
const errorMessage = ref("");
const history = ref([]);
const stats = ref({
  total_sessions: 0,
  graded_sessions: 0,
  average_band: null,
  sessions_by_skill: {},
});

const skillCount = computed(
  () => Object.keys(stats.value.sessions_by_skill || {}).length
);

const formatStatus = (s) =>
  ({
    in_progress: "In progress",
    submitted: "Submitted",
    graded: "Graded",
    expired: "Expired",
    abandoned: "Abandoned",
  }[s] || s);

const statusClass = (s) =>
  ({
    in_progress: "bg-secondary",
    submitted: "bg-info text-dark",
    graded: "bg-success",
    expired: "bg-warning text-dark",
    abandoned: "bg-light text-dark",
  }[s] || "bg-secondary");

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

async function load() {
  await init();
  if (!isAuthenticated.value) return;

  loading.value = true;
  errorMessage.value = "";
  try {
    const [historyData, statsData] = await Promise.all([
      getHistory({ page: 1, size: 50 }),
      getStatistics(),
    ]);
    history.value = historyData.items;
    stats.value = statsData;
  } catch (e) {
    errorMessage.value = handleError(e, "PracticeRecords.load");
  } finally {
    loading.value = false;
  }
}

onMounted(load);
</script>

<style scoped>
.page-title {
  font-weight: 500;
  color: #1e40af;
}
.stat-card {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 18px;
  text-align: center;
  box-shadow: 0 0 18px rgba(0, 0, 0, 0.04);
  height: 100%;
}
.stat-value {
  font-size: 1.8rem;
  font-weight: 700;
  color: #1e40af;
}
.stat-label {
  font-size: 0.85rem;
  color: #6c757d;
}
.records-table th {
  font-size: 0.85rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
</style>
